import React, { useEffect, useState } from "react";
import { Building, Phone, Mail, MapPin, Save, Edit2, X, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function Kurumsal() {
  const { hasPerm } = useAuth();
  const [company, setCompany] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [newService, setNewService] = useState("");

  const load = async () => {
    const { data } = await api.get("/company");
    setCompany(data);
    setForm(data);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const { data } = await api.patch("/company", form);
      setCompany(data);
      setEditing(false);
      toast.success("Kurumsal bilgiler güncellendi");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addService = () => {
    if (!newService.trim()) return;
    setForm({ ...form, services: [...(form.services || []), newService.trim()] });
    setNewService("");
  };

  const removeService = (idx) => {
    setForm({ ...form, services: form.services.filter((_, i) => i !== idx) });
  };

  if (!company) return <div className="text-muted-foreground text-sm">Yükleniyor…</div>;

  return (
    <div className="space-y-6" data-testid="kurumsal-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Şirket</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Kurumsal Bilgi</h1>
        </div>
        {hasPerm("kurumsal.edit") && !editing && (
          <Button onClick={() => { setForm(company); setEditing(true); }} data-testid="edit-company-btn" variant="outline">
            <Edit2 className="w-4 h-4 mr-2" /> Düzenle
          </Button>
        )}
        {editing && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setEditing(false); setForm(company); }}>İptal</Button>
            <Button onClick={save} data-testid="save-company-btn"><Save className="w-4 h-4 mr-2" />Kaydet</Button>
          </div>
        )}
      </div>

      <div className="relative overflow-hidden rounded-md">
        <img
          src="https://images.unsplash.com/photo-1486718448742-163732cd1544?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb25zdHJ1Y3Rpb24lMjBhcmNoaXRlY3R1cmUlMjBidWlsZGluZ3xlbnwwfHx8fDE3ODUwMjcyNzl8MA&ixlib=rb-4.1.0&q=85"
          alt="MMF Grup"
          className="w-full h-52 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 p-6 text-white">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2 text-brand">
            {editing ? (
              <Input value={form.tagline || ""} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="bg-black/30 border-white/30 text-white h-8 text-xs" />
            ) : company.tagline}
          </div>
          <div className="font-heading text-3xl font-bold tracking-tight">
            {editing ? (
              <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/30 border-white/30 text-white text-2xl h-11" />
            ) : company.name}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-5 shadow-none rounded-md md:col-span-2">
          <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-3">Hakkında</div>
          {editing ? (
            <Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={6} />
          ) : (
            <p className="text-sm leading-relaxed text-foreground/90">{company.description}</p>
          )}
          <div className="mt-4 pt-4 border-t border-border">
            <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-2">Kuruluş</div>
            {editing ? (
              <Input value={form.established || ""} onChange={(e) => setForm({ ...form, established: e.target.value })} className="max-w-[120px]" />
            ) : (
              <div className="font-heading text-2xl font-bold tracking-tight">{company.established}</div>
            )}
          </div>
        </Card>

        <Card className="p-5 shadow-none rounded-md space-y-3">
          <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">İletişim</div>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <Phone className="w-4 h-4 mt-0.5 text-brand shrink-0" />
              {editing ? (
                <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-8" />
              ) : (
                <span>{company.phone}</span>
              )}
            </div>
            <div className="flex items-start gap-3">
              <Mail className="w-4 h-4 mt-0.5 text-brand shrink-0" />
              {editing ? (
                <Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-8" />
              ) : (
                <span>{company.email}</span>
              )}
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 mt-0.5 text-brand shrink-0" />
              {editing ? (
                <Textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} />
              ) : (
                <span>{company.address}</span>
              )}
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-5 shadow-none rounded-md">
        <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-4">Hizmetler</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {(editing ? form.services : company.services)?.map((s, i) => (
            <div key={i} className="flex items-center gap-2 p-3 bg-secondary rounded-md">
              <Building className="w-4 h-4 text-brand shrink-0" />
              <span className="text-sm flex-1">{s}</span>
              {editing && (
                <button onClick={() => removeService(i)} className="text-muted-foreground hover:text-destructive">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
        {editing && (
          <div className="mt-4 flex gap-2">
            <Input value={newService} onChange={(e) => setNewService(e.target.value)} placeholder="Yeni hizmet…" onKeyDown={(e) => e.key === "Enter" && addService()} />
            <Button onClick={addService} variant="outline"><Plus className="w-4 h-4" /></Button>
          </div>
        )}
      </Card>
    </div>
  );
}
